import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import { NgForOf, NgIf } from '@angular/common';
import { first, get } from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { CalendarOptions } from '@fullcalendar/core';
import { CommonVerbsApiService } from '@services/common/common-verbs-api.service';
import { Community } from '@models/communities/community.model';
import dayGridPlugin from '@fullcalendar/daygrid';
import listWeek from '@fullcalendar/list';
import moment from 'moment';
import timeGridDay from '@fullcalendar/timegrid';

@Component({
  standalone: true,
  selector: 'app-public-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  imports: [FullCalendarModule, ReactiveFormsModule, NgForOf, NgIf],
  providers: [CommonVerbsApiService],
  encapsulation: ViewEncapsulation.None,
})
export class CalendarComponent implements OnInit {
  oldCommunityId: string | null = null;
  careTypeId: string | null = null;
  community: Community | null = null;
  careTypes: Array<any>;
  selectedCareTypeId: string;
  logo: string | null = null;
  monthDay: string;
  events: any;
  originalEvents: Array<any>;

  careTypeForm = new FormGroup({
    careTypeId: new FormControl(),
  });

  // Calendar
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;

  calendarOptions: CalendarOptions;

  public getScreenWidth: any;
  public isSmallView = false;

  constructor(
    private route: ActivatedRoute,
    private api: CommonVerbsApiService
  ) {}

  ngOnInit(): void {
    this.oldCommunityId = this.route.snapshot.url[1].path;
    this.careTypeId = get(this.route.snapshot, 'url.3.path', null);
    if (this.oldCommunityId) {
      this.initSizeCalendar();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.initSizeCalendar();
    this.resizeCalendar();
  }

  initSizeCalendar() {
    const screenWidth = window.innerWidth;
    let initialView = 'dayGridMonth';
    this.isSmallView = false;
    if (screenWidth < 1340) {
      this.isSmallView = true;
      initialView = 'listMonth';
    }
    this.calendarOptions = {
      plugins: [dayGridPlugin, timeGridDay, listWeek],
      initialView: initialView,
      eventContent: (v, el) => this.eventContent(v, el),
      events: [],
      headerToolbar: {
        left: '',
        center: '',
        right: '',
      },
    };
    this.getContent();
  }

  resizeCalendar() {
    if (this.calendarComponent !== undefined) {
      const calendar = this.calendarComponent.getApi();
      this.getScreenWidth = window.innerWidth;
      if (this.getScreenWidth < 1340) {
        this.isSmallView = true;
        calendar.changeView('listMonth');
      } else {
        this.isSmallView = false;
        calendar.changeView('dayGridMonth');
      }
    }
  }

  onChanges(): void {
    this.careTypeForm.valueChanges.subscribe(val => {
      this.selectedCareTypeId = val.careTypeId;
      this.getVisibleEvents();
    });
  }

  getContent() {
    this.monthDay = moment().utc().startOf('month').format('MMMM, YYYY');
    this.api
      .get<Community>(`communities/get-by-old-id/${this.oldCommunityId}`)
      .subscribe(community => {
        this.community = community;
        this.careTypes = get(this.community, 'careTypes', []);
        const initialCareType = first(this.careTypes);
        this.selectedCareTypeId = initialCareType.id;
        this.careTypeForm.patchValue({ careTypeId: this.selectedCareTypeId });
        this.logo = get(
          this.community,
          'corporation.calendarLogoMedia.url',
          get(
            this.community,
            'logoMedia.url',
            'https://tsolife.com/assets/images/brands/tsolife-logo-200x45.svg'
          )
        );
        this.getEvents();
      });
  }

  getEvents() {
    const startDt = moment().utc().startOf('month').format('YYYY-MM-DD');
    const endDt = moment()
      .utc()
      .endOf('month')
      .add(1, 'days')
      .format('YYYY-MM-DD');
    this.api
      .get<Array<any>>(`communities/${this.community?.id}/get-events`, {
        startDt,
        endDt,
      })
      .subscribe(events => {
        this.originalEvents = events;
        this.getVisibleEvents();
        this.onChanges();
      });
  }

  getVisibleEvents() {
    // If comes care type from URL
    if (this.careTypeId) {
      console.log('loaded Care Type' + this.careTypeId);
      const getCT = this.careTypes.find(
        careType => careType.name === this.careTypeId
      );
      if (getCT) {
        this.selectedCareTypeId = getCT.id;
      }
    }

    this.calendarOptions.events = this.originalEvents
      .filter(event => {
        const careTypes = get(event, 'care_types', []).map(
          (careType: any) => careType.id
        );
        return careTypes.indexOf(this.selectedCareTypeId) !== -1;
      })
      .map(event => {
        return {
          title: event.eventName,
          start: moment(event.startsAt).utc().format('YYYY-MM-DD HH:mm:ss'),
          end: event.endsAt,
          allDay: event.isAllDayInd,
          backgroundColor: event.color,
          extendedProps: event,
        };
      });
  }

  eventContent(arg: any, el: any) {
    console.log('eventContent');
    const colorDot =
      arg.event.allDay ||
      arg.view.type === 'timeGridWeek' ||
      arg.view.type === 'timeGridDay'
        ? '#ffffff'
        : arg.backgroundColor;

    const eventTitle = `${arg.timeText} ${arg.event.title}`;
    const eventWrapper = document.createElement('div');
    eventWrapper.className = 'd-flex w-100 text-start';
    eventWrapper.innerHTML = this.isSmallView
      ? ''
      : `<div class="fc-daygrid-event-dot" style="border-color:${colorDot}"></div>`;
    eventWrapper.innerHTML += `<span class="text-wrap">${eventTitle}</span>`;
    const arrayOfDomNodes = [eventWrapper];
    el = { domNodes: arrayOfDomNodes };
    return el;
  }
}
